import { useEffect, useState } from "react";
import { useUser } from "../../context/UserContext";
import { openUpgradeModal } from "../../lib/upgradeModal";
import "./TrialBanner.scss";

export function formatTrialTimeLeft(expiresAt: string, now: number = Date.now()): string {
  const msLeft = new Date(expiresAt).getTime() - now;
  if (msLeft <= 0) {
    return "ended";
  }
  const hoursLeft = Math.ceil(msLeft / (60 * 60 * 1000));
  if (hoursLeft < 24) {
    return `ends in ${hoursLeft} hour${hoursLeft === 1 ? "" : "s"}`;
  }
  const daysLeft = Math.ceil(hoursLeft / 24);
  return `ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`;
}

const REVEAL_DELAY_MS = 500;
// Every page renders its own Topbar, so only the first appearance per app load is delayed and animated.
let hasRevealed = false;

/** Compact free-trial status shown in the top bar during a trial, and a way to buy once it has ended. */
const TrialBanner = () => {
  const { trial, isOnTrial, hasTrialEnded } = useUser();
  const [now, setNow] = useState(() => Date.now());
  const [isRevealed, setIsRevealed] = useState(hasRevealed);
  const [animateIn] = useState(!hasRevealed);
  const shouldShow = trial !== null && (isOnTrial || hasTrialEnded);
  const className = animateIn ? "trial-banner trial-banner--entering" : "trial-banner";

  useEffect(() => {
    if (!shouldShow || isRevealed) {
      return;
    }
    const timeout = window.setTimeout(() => {
      hasRevealed = true;
      setIsRevealed(true);
    }, REVEAL_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [shouldShow, isRevealed]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(interval);
  }, []);

  if (!trial || !shouldShow || !isRevealed) {
    return null;
  }

  if (hasTrialEnded) {
    return (
      <div className={className} role="status">
        <span className="trial-banner__badge">Trial ended</span>
        <span className="trial-banner__text">Get full access to every question</span>
        <button type="button" className="trial-banner__cta trial-banner__cta--buy" onClick={openUpgradeModal}>
          Buy full access
        </button>
      </div>
    );
  }

  return (
    <div className={className} role="status">
      <span className="trial-banner__badge">Free trial</span>
      <span className="trial-banner__text">
        {trial.used} of {trial.limit} questions used, {formatTrialTimeLeft(trial.expiresAt, now)}
      </span>
      <button type="button" className="trial-banner__cta" onClick={openUpgradeModal}>
        Upgrade now
      </button>
    </div>
  );
};

export default TrialBanner;
